import type { OpenFile, Vfs } from '../types/vfs';
import { invariant } from '../util/invariant';
import { partialFilePath } from './partialFilePath';
import type { StructuredLogger } from '@mimir/primitive/structuredLogger';
import { writeFileContent } from './writeFile';

export function readableFileStream(vfs: Vfs, inputPath: string, fileOffset: number = 0, readSize: number | null = null): ReadableStream<Uint8Array> {
  let openFile: OpenFile | undefined;
  const buf = new Uint8Array(1024 * 1024);
  let remain = readSize;
  return new ReadableStream({
    async start() {
      openFile = await vfs.open(inputPath, 'r');
    },
    async pull(controller) {
      invariant(openFile, 'Expected openFile');
      const toRead = Math.min(buf.byteLength, remain ?? buf.byteLength);
      const got = await openFile.read(buf, 0, toRead, fileOffset);
      if (got < 1) {
        controller.close();
        await openFile.close();
        return;
      }
      if (remain !== null) {
        remain -= got;
      }
      const bytes = buf.slice(0, got);
      controller.enqueue(bytes);
      fileOffset += got;
    },
    cancel() {
      if (openFile !== undefined) {
        void openFile.close().catch(() => { });
      }
    },
  });
}


export function loggingWritableFileStream(vfs: Vfs, structuredLogger: StructuredLogger, outPath: string, knownSize: number) {
  const id = 'write:' + outPath;
  const temp = partialFilePath(outPath);
  let openTemp: OpenFile | undefined;
  let position = 0;
  const sink: UnderlyingSink = {
    async start() {
      openTemp = await vfs.open(temp, 'w');
      if (knownSize != null) {
        structuredLogger.log({ message: 'progress', progress: { [id]: { length: knownSize } } });
      }
    },
    async write(chunk: Uint8Array) {
      invariant(openTemp, 'Expected openTemp');
      let written = 0;
      while (true) {
        const left = chunk.byteLength - written;
        if (left == 0) {
          break;
        }
        const result = await openTemp.write(chunk, written, chunk.byteLength - written, position);
        written += result;
        position += result;
        structuredLogger.log({ message: 'progress', progress: { [id]: { position } } });
      }
    },
    async abort() {
      if (openTemp) {
        await openTemp.close();
        openTemp = undefined;
      }
    },
    async close() {
      invariant(openTemp, 'Expected openTemp');
      await openTemp.close();
      openTemp = undefined;
      await vfs.rename(temp, outPath);
      structuredLogger.log({ message: 'progress', progress: { [id]: { ended: true } } });
    }
  };
  return new WritableStream(sink);
}


export function writableFileStream(
  logger: StructuredLogger,
  vfs: Vfs,
  outPath: string,
  growingFile: boolean,
): WritableStream<Uint8Array> {
  const targetFilename = growingFile ? outPath : partialFilePath(outPath);
  let openFile: OpenFile | undefined;
  let fd : number | undefined; // for logging
  let position = 0;

  // We try to flush out to file system in bigger chunks for performance
  let bufferSize = 0;
  let buffers : Uint8Array[] = [];
  let mergedBuffer = new Uint8Array(0);
  const writeChunkSize = 1024 * 1024;

  const flush = async () => {
    if (bufferSize == 0) {
      return;
    }

    if (mergedBuffer.length < bufferSize) {
      mergedBuffer = new Uint8Array(bufferSize * 2);
    }
    let toWrite = 0;
    for (const buffer of buffers) {
      mergedBuffer.set(buffer, toWrite);
      toWrite += buffer.length;
    }
    bufferSize = 0;
    buffers = [];
    invariant(openFile, 'Expected openFile');
    await writeFileContent(openFile, mergedBuffer.subarray(0, toWrite), position);
    position += toWrite;

  };

  const sink: UnderlyingSink = {
    async start() {
      openFile = await vfs.open(targetFilename, 'w');
      fd = (openFile as any).handle?.fd;
      logger.log({ level: 'info', message: 'start writableFileStream', outPath, fd });
    },
    async write(chunk: Uint8Array) {
      buffers.push(chunk);
      bufferSize += chunk.length;
      if (bufferSize > writeChunkSize) {
        await flush();
      }
    },
    async abort() {
      logger.log({ level: 'warn', message: 'abort writableFileStream', outPath, fd });
      if (openFile) {
        await openFile.close();
        openFile = undefined;
      }
    },
    async close() {
      await flush();
      invariant(openFile, 'Expected openFile');
      await openFile.close();
      if (targetFilename !== outPath) {
        await vfs.rename(targetFilename, outPath);
      }
    }
  };
  return new WritableStream<Uint8Array>(sink);
}
